import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "@/lib/apiClient";
import { toast } from "sonner";

export interface State {
  id: number;
  nome: string;
  uf: string;
  paisId?: number | null;
  codigoIbge?: number | null;
  isAtivo: boolean;
  pais?: { id: number; nome: string } | null;
  aliquotaIbs: number;
}

export interface StateInsert {
  nome: string;
  uf: string;
  paisId?: number | null;
  codigoIbge?: number | null;
  isAtivo?: boolean;
  aliquotaIbs?: number;
}

export const useStates = () => {
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: ["states"],
    queryFn: async () => {
      const { data } = await apiClient.get<State[]>("/api/estados");
      return data;
    },
  });

  const activeQuery = useQuery({
    queryKey: ["states", "active"],
    queryFn: async () => {
      const { data } = await apiClient.get<State[]>("/api/estados", { params: { ativo: true } });
      return data;
    },
  });

  const createMutation = useMutation({
    mutationFn: async (body: StateInsert) => {
      const { data } = await apiClient.post<State>("/api/estados", body);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["states"] });
      toast.success("Estado criado com sucesso!");
    },
    onError: (error: Error) => {
      toast.error(`Erro ao criar estado: ${error.message}`);
    },
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, data }: { id: number; data: Partial<StateInsert> }) => {
      const { data: updated } = await apiClient.put<State>(`/api/estados/${id}`, data);
      return updated;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["states"] });
      toast.success("Estado atualizado com sucesso!");
    },
    onError: (error: Error) => {
      toast.error(`Erro ao atualizar estado: ${error.message}`);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: number) => {
      await apiClient.delete(`/api/estados/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["states"] });
      toast.success("Estado excluído com sucesso!");
    },
    onError: (error: Error) => {
      toast.error(`Erro ao excluir estado: ${error.message}`);
    },
  });

  return {
    states: query.data,
    activeStates: activeQuery.data,
    isLoading: query.isLoading,
    error: query.error,
    createState: createMutation.mutate,
    updateState: updateMutation.mutate,
    deleteState: deleteMutation.mutate,
    isCreating: createMutation.isPending,
    isUpdating: updateMutation.isPending,
    isDeleting: deleteMutation.isPending,
  };
};
